import {
  Box,
  Divider,
  Grid,
  Menu,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import BlockIcon from '@mui/icons-material/Block'
import React, { useMemo } from 'react'
import {
  Datagrid,
  Filter,
  FunctionField,
  ListProps,
  Pagination,
  ReferenceField,
  TextField,
  TextInput,
  sanitizeFetchType,
  useQuery,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import Button from '../../common/customized-mui-components/Button'
import List from '../../common/customized-ra-components/List'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import FilterProps from '../../common/FilterProps'
import QuickFilter from '../../common/QuickFilter'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import {
  CheckIfAnyModuleHasError,
  CheckLastRefreshError,
} from '../common/modules/ModuleStatus'
import { PalmAttachEventRecurringBulkButton } from './palm-list/PalmAttachEventRecurringBulkButton'
import { PalmDetachEventRecurringBulkButton } from './palm-list/PalmDetachEventRecurringBulkButton'
import { PalmLockBulkButton } from './palm-list/PalmLockBulkButton'
import { PalmUnlockBulkButton } from './palm-list/PalmUnlockBulkButton'
import { PalmStatus } from './palm-show/PalmStatus'
import { DeviceCategoryExtendedDto } from '../../../core/dto/device-category/device-category-extended.dto'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { DeviceStatusCheck } from '../../../core/enum/DeviceStatusCheck'
import { useColorPalette } from '../../../core/devices/devices.utils'
import { TextFieldWithMaxChars } from '../../common/TextFieldWithMaxChars'

const PalmFilters = (props: FilterProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()
  return (
    <Filter {...props}>
      <TextInput source="name" alwaysOn autoFocus />
      {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
        <FilteredReferenceInput
          label="resources.palms.fields.categoryId"
          source="categoryIdWithDescendants"
          reference={ResourceName.DEVICE_CATEGORIES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'hierarchyString',
            showFilter: true,
          }}
        />
      )}
      <QuickFilter source="eventActive" defaultValue />
    </Filter>
  )
}

const PalmGridExpand = ({ ...props }) => {
  const { record: palm } = props
  const t = useTranslate()
  const hasAuthority = useHasAuthority()
  return (
    <Box padding={1}>
      {palm?.categoryId && (
        <Grid container spacing={1}>
          <Grid item xs={4}>
            {t('resources.palms.fields.categoryId')}
          </Grid>
          <Grid item xs={8} style={{ textAlign: 'center' }}>
            {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
              <ReferenceField
                source="categoryId"
                reference={ResourceName.DEVICE_CATEGORIES}
                link={false}
                sortBy="name"
              >
                <FunctionField<DeviceCategoryExtendedDto>
                  source="hierarchyString"
                  render={(record?: DeviceCategoryExtendedDto) =>
                    `${record?.id}. ${record?.hierarchyString}`
                  }
                />
              </ReferenceField>
            )}
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      )}
      {hasAuthority(Authority.VIEW_VERIFIER_DEVICE_DISABLED) &&
        palm?.disabledId && (
          <Grid container spacing={1}>
            <Grid item xs={4}>
              {t('resources.palms.fields.disabled')}
            </Grid>
            <Grid item xs={8} style={{ textAlign: 'center' }}>
              <ReferenceField
                source="disabledId"
                reference={ResourceName.VERIFIER_DEVICE_DISABLED}
                link={false}
                sortBy="name"
                label="resources.palms.fields.disabled"
              >
                <FunctionField
                  source="name"
                  render={(record) => `${record?.id}. ${record?.name}`}
                />
              </ReferenceField>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        )}
      {palm?.mobileDeviceId && (
        <Grid container spacing={1}>
          <Grid item xs={4}>
            {t('resources.palms.fields.mobileDeviceId')}
          </Grid>
          <Grid item xs={8} style={{ textAlign: 'center' }}>
            <TextField source="mobileDeviceId" />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      )}
      {palm?.jsonStatus && (
        <Grid item xs={12}>
          <PalmStatus {...props} hideDevices />
        </Grid>
      )}
    </Box>
  )
}

const PalmsBulkActions = ({ ...props }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const PalmLockAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <PalmLockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const PalmUnlockAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <PalmUnlockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const PalmLockRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <PalmLockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const PalmUnlockRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <PalmUnlockBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const PalmAttachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <PalmAttachEventRecurringBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const PalmDetachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <PalmDetachEventRecurringBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  return (
    <div>
      <Button
        onClick={handleClick}
        label="const.selectAction"
        variant="text"
        color="default"
        useSmallVersionBreakpoint={false}
      />
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
      >
        <PalmLockAction />
        <PalmLockRecurringAction />
        <PalmUnlockAction />
        <PalmUnlockRecurringAction />
        <PalmAttachEventRecurringAction />
        <PalmDetachEventRecurringAction />
      </Menu>
    </div>
  )
}

export const PalmList = (props: ListProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()
  const colorPalette = useColorPalette(theme)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  const postRowStyle = (record: any) => {
    if (!serverDate) return {}

    const palmError = CheckLastRefreshError(
      record?.lastRefresh,
      serverDate?.serverDateTime,
    )
    if (palmError) return colorPalette(DeviceStatusCheck.ERROR)

    let palmWarning = false
    palmWarning = CheckIfAnyModuleHasError(
      record.jsonStatus,
      DeviceTypes.PALM,
      serverDate?.serverDateTime,
    )
    if (record.jsonStatus) {
      try {
        const status = JSON.parse(record.jsonStatus)
        const { percentageBatteryLevel } = status
        if (percentageBatteryLevel < 20) {
          palmWarning = true
        }
      } catch {
        palmWarning = true
      }
    }
    if (palmWarning) return colorPalette(DeviceStatusCheck.WARNING)
    return colorPalette(DeviceStatusCheck.SUCCESS)
  }

  return (
    <List
      {...props}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      exporter={false}
      filters={<PalmFilters />}
      bulkActionButtons={<PalmsBulkActions />}
    >
      <Datagrid
        rowStyle={postRowStyle}
        size="small"
        rowClick={(id) => `${ResourceName.PALMS}/${id}/show`}
        isRowExpandable={(record) =>
          (record?.jsonStatus !== undefined && record?.jsonStatus !== null) ||
          (smallScreen && (record?.categoryId || record?.mobileDeviceId))
        }
        expand={<PalmGridExpand />}
      >
        <TextField source="id" label="ID" />
        <TextFieldWithMaxChars source="name" maxChars={smallScreen ? 10 : 64} />
        {!smallScreen && hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link={false}
            sortBy="name"
          >
            <FunctionField<DeviceCategoryExtendedDto>
              source="hierarchyString"
              render={(record?: DeviceCategoryExtendedDto) =>
                `${record?.id}. ${record?.hierarchyString}`
              }
            />
          </ReferenceField>
        )}
        {(hasAuthority(Authority.VIEW_VERIFIER_DEVICE_DISABLED) &&
          !smallScreen && (
            <ReferenceField
              source="disabledId"
              reference={ResourceName.VERIFIER_DEVICE_DISABLED}
              link={false}
              sortBy="name"
              label="resources.palms.fields.disabled"
            >
              <FunctionField
                source="name"
                render={(record) => `${record?.id}. ${record?.name}`}
              />
            </ReferenceField>
          )) || (
          <ReferenceField
            source="disabledId"
            reference={ResourceName.VERIFIER_DEVICE_DISABLED}
            link={false}
            label={<BlockIcon />}
          >
            <BlockIcon />
          </ReferenceField>
        )}
        {!smallScreen && <TextField source="mobileDeviceId" sortable={false} />}
      </Datagrid>
    </List>
  )
}
